import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Button,
  Table,
  Box,
  StatusIndicator,
  TextFilter,
} from '@cloudscape-design/components';
import { Task, SearchTasksRequest } from 'src/types/helios/Task';
import { TaskStatus } from '@amzn/gbsa-helios-typescript-client';
import { ExportButton } from 'src/components/ExportButton';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import { ApiRequestStatus } from 'src/types/ApiRequestStatus';
import AppPagination, { AppPaginationRef } from 'src/components/AppPagination';
import { PaginationConfig } from 'src/contstant/PaginationConfig';
import useUserProfile from 'src/hooks/useUserProfile';
import {
  getExportTasksConfig,
  getTasksTableDefinitions,
  defaultVisibleColumns,
} from './taskConfig';
import { useUsers } from 'src/store/users/useUsers';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { useTasks } from 'src/store/tasks/useTasks';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import TaskFilterView from 'src/components/task/TaskFilterView';
import { DEFAULT_LANGUAGE, PAGE_SIZE_OPTIONS } from 'src/contstant/commonLabels';
import TablePreferences, { TablePreferencesRef } from 'src/components/TablePreferences';
import { LanguageDropdown } from 'src/components/LanguageDropdown';
import { countActiveFilters } from 'src/utils/filterPersistence';
import { useCallback } from 'react';
import { getPageFilterKey } from 'src/contstant/storageKeys';
import { usePageFilters } from 'src/hooks/usePageFilters';
import { SharedModeBanner } from 'src/components/SharedModeBanner';
import { BusinessUnitMismatchHandler } from 'src/components/BusinessUnitMismatchHandler';
import { ShareFiltersButton } from 'src/components/ShareFiltersButton';

const TasksPage: FC = () => {
  const navigate = useNavigate();
  const useRefPagiantion = useRef<AppPaginationRef>(null);
  const tablePreferencesRef = useRef<TablePreferencesRef>(null);

  const {
    getUserProgramId,
    getUserBusinessUnitId,
    getLocations,
    selectUserLanguage,
    getUserSelectedLanguage,
    getPreferLanguageList,
  } = useUserProfile();
  const { currentUser } = useUsers();
  const { setContent, setTitle, closeView, content, setPanelSize } = useSplitPanel();
  const { businessUnitList, loadBusinessUnits } = useBusinessUnit();

  // Get business unit ID for scoped filter persistence
  const businessUnitId = getUserBusinessUnitId();

  const [shareFilterStatus, setShareFilterStatus] = useState<ApiRequestStatus[]>([]); // Share filter status messages

  // Stable reference to default filter generator
  const getDefaultFilters = useCallback((): SearchTasksRequest => {
    return {
      businessUnitId: getUserBusinessUnitId(),
      locations: getLocations(),
    };
  }, [getUserBusinessUnitId, getLocations]);

  // Use page filters hook with shared URL support
  const {
    filters: searchParams,
    setFilters: setSearchParams,
    clearFilters,
    resetTarget,
    isSharedMode,
    isBUMismatch,
  } = usePageFilters({
    storageKey: getPageFilterKey(businessUnitId, 'tasks'),
    defaultFilters: getDefaultFilters(),
    excludeFields: [
      'businessUnitId',
      'maxResults',
      'nextToken',
      'language',
      'additionalLanguages',
    ],
    universalFields: ['locations', 'divisionName'], // These sync across all pages
    currentBusinessUnitId: businessUnitId,
  });

  // Count active filters using generic utility
  const activeFilterCount = useMemo(() => {
    return countActiveFilters(searchParams, [
      'businessUnitId',
      'maxResults',
      'nextToken',
      'language',
      'additionalLanguages',
    ]);
  }, [searchParams]);

  const {
    tasksList,
    searchTasks,
    fetchTasksApiStatus,
    searchTasksApiStatus,
    clearAPIStatusMessage,
  } = useTasks();

  // Filter out draft tasks — drafts are only visible in the perform audit page
  const displayedTasks = useMemo(
    () => tasksList?.filter((t) => t.taskStatus !== TaskStatus.DRAFT) ?? [],
    [tasksList],
  );

  // Table configuration
  const allColumnDefinition = useMemo(
    () =>
      getTasksTableDefinitions(businessUnitList, getUserSelectedLanguage()).filter(
        (col) => col.id !== undefined,
      ),
    [businessUnitList, getUserSelectedLanguage()],
  );
  // Table preferences state
  const [tablePrefs, setTablePrefs] = useState<{
    visibleColumns: any[];
    paginatedItems: Task[];
    currentPage: number;
    pageSize: number;
  }>({
    visibleColumns: [],
    paginatedItems: [],
    currentPage: 1,
    pageSize: 0,
  });

  // Handler for table preferences changes
  const handlePreferencesChange = (data: {
    visibleColumns: any[];
    paginatedItems: any[];
    currentPage: number;
    pageSize: number;
  }) => {
    // useRefPagiantion?.current?.clearTokens()
    setTablePrefs((prev) => ({
      ...prev,
      visibleColumns: data.visibleColumns,
      currentPage: data.currentPage,
      pageSize: data.pageSize as PaginationConfig,
      paginatedItems: data.paginatedItems as typeof prev.paginatedItems,
    }));
  };

  useEffect(() => {
    // Load business units for name lookup
    loadBusinessUnits({
      programId: getUserProgramId(),
      isActive: true,
      isDeleted: false,
    });

    return () => {
      closeView();
    };
  }, [
    currentUser,
    getUserBusinessUnitId(),
    JSON.stringify(getLocations()),
    JSON.stringify(getPreferLanguageList()),
  ]);

  // Reload data when search parameters change
  useEffect(() => {
    // Don't load data if there's a BU mismatch
    if (isBUMismatch) {
      return;
    }
    loadData(undefined, tablePrefs.pageSize);
  }, [currentUser, searchParams, tablePrefs.pageSize, isBUMismatch]);

  //when changing the language from dropdown , it refresh the content
  useEffect(() => {
    tablePreferencesRef.current?.refreshColumns();
  }, [getUserSelectedLanguage()]);

  /**
   * Loads tasks data with pagination and search parameters
   */
  const loadData = (nextToken?: string, maxResults?: number) => {
    if (maxResults === 0) {
      return;
    }
    if (!nextToken) {
      useRefPagiantion?.current?.clearTokens();
    }
    searchTasks({
      ...searchParams,
      maxResults: maxResults,
      nextToken: nextToken,
      businessUnitId: getUserBusinessUnitId(),
      language: DEFAULT_LANGUAGE,
      additionalLanguages: getPreferLanguageList(),
    });
  };

  const openFilterView = () => {
    setPanelSize(330);
    setTitle('Filter');
    setContent(
      <TaskFilterView
        onSearchChange={function (searchParams: SearchTasksRequest): void {
          setSearchParams(searchParams);
        }}
        currentSearchParams={searchParams}
        resetSearchParams={resetTarget}
        onClearAll={clearFilters}
      />,
    );
  };

  // Update split panel content when searchParams changes (for cross-tab sync)
  useEffect(() => {
    if (
      content &&
      typeof content === 'object' &&
      'type' in content &&
      content.type === TaskFilterView
    ) {
      setContent(
        <TaskFilterView
          onSearchChange={function (searchParams: SearchTasksRequest): void {
            setSearchParams(searchParams);
          }}
          currentSearchParams={searchParams}
          resetSearchParams={resetTarget}
          onClearAll={clearFilters}
        />,
      );
    }
  }, [searchParams]);

  return (
    <ContentLayout>
      {/* Business Unit Mismatch Handler - automatically handles BU mismatch scenarios */}
      <BusinessUnitMismatchHandler
        isSharedMode={isSharedMode}
        isBUMismatch={isBUMismatch}
        currentBusinessUnitId={businessUnitId}
        requiredBusinessUnitId={searchParams?.businessUnitId}
        returnPath="/tasks"
      />

      <ApiStatusFlashBar
        apiStatus={[
          ...shareFilterStatus,
          searchParams ? searchTasksApiStatus : fetchTasksApiStatus,
        ]}
        clearMessages={() => {
          clearAPIStatusMessage();
          setShareFilterStatus([]);
        }}
      />

      <SharedModeBanner isSharedMode={isSharedMode} isBUMismatch={isBUMismatch} />

      <Box margin={{ top: 's' }}>
        <Table
          data-testid="tasks-table"
          columnDefinitions={tablePrefs.visibleColumns}
          items={displayedTasks}
          loading={searchTasksApiStatus.isLoading}
          loadingText="Loading Tasks..."
          header={
            <Header
              variant="h1"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {/* Refresh data button */}
                  <Button
                    data-testid="refresh-button"
                    iconName="refresh"
                    onClick={() => loadData(undefined, tablePrefs.pageSize)}
                  />
                  <LanguageDropdown
                    data-testid="task-language-dropdown"
                    selectedLanguage={getUserSelectedLanguage()}
                    defaultLanguage={DEFAULT_LANGUAGE}
                    filterLanguges={getPreferLanguageList()}
                    onLanguageChange={(value) => {
                      selectUserLanguage(value);
                    }}
                  />
                  <ExportButton
                    data={tasksList}
                    config={getExportTasksConfig(businessUnitList)}
                    onError={(error) => {
                      alert(error);
                    }}
                  />
                  {!isBUMismatch && (
                    <ShareFiltersButton
                      data-testid="share-filters-button"
                      filters={searchParams}
                      businessUnitId={businessUnitId}
                      onStatusChange={setShareFilterStatus}
                    />
                  )}
                  {!content && !isBUMismatch && (
                    <Button
                      data-testid="filter-button"
                      iconName="filter"
                      onClick={openFilterView}
                    >
                      {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
                    </Button>
                  )}
                </SpaceBetween>
              }
            >
              Tasks
            </Header>
          }
          filter={
            <SpaceBetween size="m" direction="horizontal">
              <TextFilter
                filteringText={searchParams?.taskName || ''}
                filteringPlaceholder="Search Tasks"
                filteringAriaLabel="Search Tasks"
                onChange={({ detail }) => {
                  setSearchParams((prev) => ({
                    ...prev!,
                    taskName:
                      detail.filteringText === '' ? undefined : detail.filteringText,
                  }));
                }}
              />
            </SpaceBetween>
          }
          pagination={
            <AppPagination
              ref={useRefPagiantion}
              nextToken={searchTasksApiStatus.nextToken}
              onChange={(nextToken?: string) => {
                loadData(nextToken, tablePrefs.pageSize);
              }}
            />
          }
          preferences={
            <TablePreferences
              tableId="tasks-table"
              ref={tablePreferencesRef}
              defaultVisibleColumns={defaultVisibleColumns}
              defaultPageSize={PaginationConfig.TASKS_MAX_RESULT}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              allColumnDefinitions={allColumnDefinition as any}
              allItems={displayedTasks}
              onPreferencesChange={handlePreferencesChange}
            />
          }
          stripedRows
          stickyHeader
          empty={
            <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <StatusIndicator type="warning">No Tasks.</StatusIndicator>
              </SpaceBetween>
            </Box>
          }
        />
      </Box>
    </ContentLayout>
  );
};

export default TasksPage;
